import React, { useEffect, useMemo, useRef, useState } from "react";
import { models } from "powerbi-client";
import { PowerBIEmbed } from "powerbi-client-react";
import {
  Column,
  DataGrid,
  FilterRow,
  HeaderFilter,
  LoadPanel,
  Scrolling,
  Item,
  Toolbar,
} from "devextreme-react/data-grid";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import { OidcIdentityContext } from "@rsmus/react-auth";

import { useContext } from "react";
import { v4 as uuidv4 } from "uuid";
import { useRecoilValue } from "recoil";

import { dataGridConfig } from "../../../constants";
import { useSnackbar } from "../../../hooks";
import { REACT_APP_SYSTEM_ADMIN_USERS } from "../../../envVariables";
import { userAssignmentsState } from "../../../App";
import { envProperties } from "../../../util/EnvironmentProperties";
import { calculateGridHeight } from "../../../util/Utils";
import AccessDenied from "../../pageNotFound/AccessDenied";
import {
  CustomPowerBiDeleteResponse,
  CustomPowerBiDTO,
  CustomPowerBiRequestDTO,
} from "../../../api-client-nswag/taxportal-client";
import { taxPortalClientApi, userAssignmentClientApi } from "../../../api-client-nswag/taxportal-client-runtime";

import "./CustomPowerBISetup.scss";

type DatagridRefType = DataGrid;

type CustomPowerBISetupRecord = {
  id: string;
  powerBIName: string;
  powerBIUrl: string;
  attachmentName?: string;
  primarySME: string;
  alternateSME: string;
  roleAccess: string[];
  requestor: string;
  createdDate: string;
  publishedDate?: string;
  launchDate?: string;
  lastRefreshedDate?: string;
  reportId: string;
  datasetId: string;
  siteId: string;
  mdmClientId?: number;
  navigationName?: string;
};

type DialogMode = "add" | "edit";

type RecordDraft = {
  powerBIName: string;
  powerBIUrl: string;
  attachmentFile?: File | null;
  attachmentName?: string;
  primarySME: string;
  alternateSME: string;
  roleAccess: string[];
  reportId: string;
  datasetId: string;
  navigationName: string;
};


const getSiteIdFromSessionStorage = (): string | null => {
  const existing = sessionStorage.getItem("siteId") ?? "";
  let parsed = parseInt(existing, 10);
  if (parsed && !Number.isNaN(parsed)) return String(parsed);

  const ref = document.referrer ?? "";
  const refSite = ref.split("siteID=")[1]?.split("&")[0];
  if (refSite) {
    parsed = parseInt(refSite, 10);
    if (parsed && !Number.isNaN(parsed)) {
      sessionStorage.setItem("siteId", String(parsed));
      return String(parsed);
    }
  }

  const href = document.location?.href ?? "";
  if (href.includes("siteID=")) {
    try {
      const urlParams = new URL(href);
      const params = new URLSearchParams(urlParams.search);
      const site = params.get("siteID") ?? "";
      parsed = parseInt(site, 10);
      if (parsed && !Number.isNaN(parsed)) {
        sessionStorage.setItem("siteId", String(parsed));
        return String(parsed);
      }
    } catch {
      // ignore
    }
  }

  const raw = sessionStorage.getItem("siteId") ?? "";
  parsed = parseInt(raw, 10);
  if (!parsed || Number.isNaN(parsed)) return null;
  return String(parsed);
};

const normalizeRole = (role: string) => role.trim().toLowerCase();

const hasGroup = (groups: string[], groupName: string): boolean =>
  groups.some((g) => normalizeRole(g) === normalizeRole(groupName));

const parseRoleAccessFromDto = (dto: any): string[] => {
  const raw =
    dto?.roleAccess ??
    dto?.roleAccesses ??
    dto?.roleAccessList ??
    dto?.rolesAccess ??
    dto?.permittedRoles ??
    dto?.permittedRoleAccess;

  if (Array.isArray(raw)) {
    return raw.map((v) => String(v ?? "").trim()).filter(Boolean);
  }

  if (typeof raw === "string") {
    return raw
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeRoleAccessList = (items: string[]) => {
  const unique = Array.from(
    new Set(items.map((v) => String(v ?? "").trim()).filter(Boolean)),
  );
  unique.sort((a, b) => a.localeCompare(b));
  return unique;
};

const normalizeAccessToken = (value?: string | null) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");

const isCSRoleName = (name: string) => /^CS\s*-/i.test((name ?? "").trim());

const isTessRoleName = (name: string) => /^tess([\s_-]|$)/i.test((name ?? "").trim());

export const CustomPowerBISetup: React.FC = () => {
  const dataGridRef = useRef<DatagridRefType>(null);
  const { showSnackbar, showSuccessSnackbar } = useSnackbar();
  const showSnackbarRef = useRef(showSnackbar);
  const lastLoadedSiteIdRef = useRef<string | null>(null);
  const handledDeepLinkRef = useRef(false);
  const { user } = useContext(OidcIdentityContext);
  const userAssignments = useRecoilValue(userAssignmentsState);

  const userEmail =
    (user?.profile?.preferred_username as string | undefined) ?? "";

  const systemAdminUsers = useMemo<string[]>(() => {
    const raw = REACT_APP_SYSTEM_ADMIN_USERS;
    if (!raw) return [];

    return raw
      .split(",")
      .map((email: string) => email.replace(/"/g, "").trim().toLowerCase())
      .filter(Boolean);
  }, []);

  const isSystemAdmin = systemAdminUsers.includes(userEmail.toLowerCase());


  // Extract groups from user assignments
  const userGroups = useMemo(() => {
    const groups: string[] = [];
    for (const assignment of userAssignments ?? []) {
      const groupList = assignment.userGroups
        ? assignment.userGroups
            .split(",")
            .map((g) => g.trim())
            .filter(Boolean)
        : [];
      groups.push(...groupList);
    }
    return groups;
  }, [userAssignments]);

  // Determine user role based on group membership only
  const isL2SupportUser = isSystemAdmin || hasGroup(userGroups, "site admin");
  const isPowerUser = !isL2SupportUser && hasGroup(userGroups, "content admin");
  const canEditAndRefresh = isL2SupportUser || isPowerUser;

  const [records, setRecords] = useState<CustomPowerBISetupRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [siteAppName, setSiteAppName] = useState<string>("");
  const [siteMdmClientId, setSiteMdmClientId] = useState<number | null>(null);
  const [siteHqSiteId, setSiteHqSiteId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>("add");
  const [activeRecordId, setActiveRecordId] = useState<string | null>(null);
  const [attachmentError, setAttachmentError] = useState<string>("");
  const [actionsAnchorPosition, setActionsAnchorPosition] = useState<{ top: number; left: number } | null>(null);
  const [actionsRecord, setActionsRecord] = useState<CustomPowerBISetupRecord | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CustomPowerBISetupRecord | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isGenerateScriptDialogOpen, setIsGenerateScriptDialogOpen] = useState(false);
  const [generateScriptLoading, setGenerateScriptLoading] = useState(false);
  const [generatedScriptText, setGeneratedScriptText] = useState<string>("");
  const [generateScriptError, setGenerateScriptError] = useState<string>("");
  const [saveLoading, setSaveLoading] = useState(false);

  const [roleAccessOptions, setRoleAccessOptions] = useState<string[]>([]);
  const [isRoleAccessLoading, setIsRoleAccessLoading] = useState(false);

  const [isEmbeddedReportView, setIsEmbeddedReportView] = useState(false);
  const [isDeepLinkLoading, setIsDeepLinkLoading] = useState(false);
  const [isDeepLinkAccessDenied, setIsDeepLinkAccessDenied] = useState(false);
  const [embeddedReportConfigDTO, setEmbeddedReportConfigDTO] = useState<models.IReportEmbedConfiguration>({
    type: "report",
    embedUrl: "",
    tokenType: models.TokenType.Embed,
    accessToken: "",
    settings: undefined,
  });

  const isActionsMenuOpen = Boolean(actionsAnchorPosition);

  const userRoleTokens = useMemo(() => {
    return new Set(
      userGroups.map((g) => normalizeAccessToken(g)).filter(Boolean)
    );
  }, [userGroups]);

  useEffect(() => {
    showSnackbarRef.current = showSnackbar;
  }, [showSnackbar]);

  useEffect(() => {
    let cancelled = false;

    if (!siteAppName) return;

    (async () => {
      setIsRoleAccessLoading(true);

      try {
        const rolesResp = await userAssignmentClientApi.userAssignment_GetRoles(null);
        const rolesRespAppName = siteAppName.trim().toUpperCase();

        const allRoles: any[] = (rolesResp as any)?.data?.roles ?? [];
        let roleNames = allRoles
          .filter((r) => String(r?.level ?? "").trim().toLowerCase() === "client")
          .map((r) => String(r?.name ?? "").trim())
          .filter(Boolean)
          .filter((r) => !isTessRoleName(r));

        if (rolesRespAppName === "CS") {
          roleNames = roleNames.filter(isCSRoleName);
        } else if (rolesRespAppName === "TP") {
          roleNames = roleNames.filter((r) => !isCSRoleName(r));
        }
        const merged = normalizeRoleAccessList(roleNames);

        if (!cancelled) setRoleAccessOptions(merged);
      } catch (error: any) {
        if (!cancelled) {
          showSnackbarRef.current({
            message: "Failed to load Role Access options.",
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) setIsRoleAccessLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [siteAppName]);

  useEffect(() => {
    let cancelled = false;
    const urlParams = new URLSearchParams(window.location.search ?? "");
    const querySiteId = parseInt(urlParams.get("siteID") ?? urlParams.get("siteId") ?? "", 10);
    const queryReportId = parseInt(urlParams.get("PowerBIReport") ?? urlParams.get("reportId") ?? "", 10);

    const hasDeepLink = !Number.isNaN(querySiteId) && querySiteId > 0 && !Number.isNaN(queryReportId) && queryReportId > 0;
    if (hasDeepLink && !handledDeepLinkRef.current) {
      handledDeepLinkRef.current = true;
      setIsDeepLinkLoading(true);
      setIsDeepLinkAccessDenied(false);
      (async () => {
        try {
          if (!isSystemAdmin && userAssignments.length === 0) {
            if (!cancelled) {
              setIsDeepLinkAccessDenied(true);
              setIsEmbeddedReportView(false);
              setIsDeepLinkLoading(false);
            }
            return;
          }

          const reportResp: any = await taxPortalClientApi.reports_GetCustomPowerBiReportById(queryReportId);
          const reportDto: any = reportResp?.data?.data ?? reportResp?.data ?? reportResp ?? null;

          if (!reportDto || (!reportDto.customPowerBiID && !reportDto.customPowerBiId && !reportDto.id)) {
            if (!cancelled) {
              setIsDeepLinkAccessDenied(true);
              setIsEmbeddedReportView(false);
            }
            return;
          }
          const embedded = await taxPortalClientApi.reports_GetCustomReport(querySiteId, queryReportId, userGroups, isSystemAdmin);
          const embedUrl = String(embedded?.embedUrl ?? "").trim();
          const accessToken = String(embedded?.token ?? "").trim();

          if (!embedUrl || !accessToken) {
            showSnackbarRef.current({
              message: "No report URL or access token available.",
              severity: "error",
            });
            return;
          }

          const config: models.IReportEmbedConfiguration = { type: "report", embedUrl, tokenType: models.TokenType.Embed,
            accessToken,
            settings: {
              filterPaneEnabled: false,
              navContentPaneEnabled: false,
            },
          };

          setEmbeddedReportConfigDTO(config);
          setIsEmbeddedReportView(true);
        } catch (error: any) {
          if (error?.status === 401 || error?.status === 403) {
            if (!cancelled) {
              setIsDeepLinkAccessDenied(true);
              setIsEmbeddedReportView(false);
            }
            return;
          }
          const msg: string = String(error?.message ?? "");
          if (msg.toLowerCase().includes("doesn't have access") || msg.toLowerCase().includes("does not have access")) {
            if (!cancelled) {
              setIsDeepLinkAccessDenied(true);
              setIsEmbeddedReportView(false);
            }
            return;
          }
          showSnackbarRef.current({
            message: error?.message ?? "Failed to load embedded report URL.",
            severity: "error",
          });
        } finally {
          if (!cancelled) setIsDeepLinkLoading(false);
        }
      })();
      return;
    }

    const sessionSiteId = getSiteIdFromSessionStorage();
    if (!sessionSiteId) {
      showSnackbarRef.current({
        message: "Site ID is missing from session storage.",
        severity: "error",
      });
      return;
    }

    // Prevent re-fetch loops when component re-renders.
    if (lastLoadedSiteIdRef.current === sessionSiteId) return;
    lastLoadedSiteIdRef.current = sessionSiteId;

    (async () => {
      const siteIdNumber = parseInt(sessionSiteId, 10);
      if (!cancelled) setRecordsLoading(true);
      try {
        const resp: any = await taxPortalClientApi.reports_GetCustomPowerBiReportsBySiteId(siteIdNumber);
        const hqSiteID = resp.hqSiteId;
        const mdmCLientID = resp.mdmClientId;
        const appName = resp.appName;
        const root: any = (resp as any)?.data?.data ?? (resp as any)?.data ?? resp ?? null;
       setSiteAppName(appName);
       setSiteMdmClientId(mdmCLientID);
       setSiteHqSiteId(hqSiteID);

        const list: any[] =
          Array.isArray(root)
            ? root
            : Array.isArray(root?.data)
              ? root.data
              : Array.isArray(root?.items)
                ? root.items
                : [];

        const mapDtoToRecord = (dto: CustomPowerBiDTO | any): CustomPowerBISetupRecord => {
          const id = dto?.customPowerBiID ?? dto?.customPowerBiId ?? dto?.id ?? "";
          const hqSiteId = dto?.hqSiteId ?? dto?.hqSiteID ?? hqSiteID ?? siteIdNumber;
          const roleAccess = parseRoleAccessFromDto(dto);
          return {
            id: String(id),
            siteId: String(hqSiteId),
            mdmClientId:
              typeof dto?.mdmClientId === "number"
                ? dto.mdmClientId
                : dto?.mdmClientId
                  ? parseInt(String(dto.mdmClientId), 10)
                  : mdmCLientID  != null
                    ? mdmCLientID
                    : undefined,
            powerBIName: String(dto?.powerBIName ?? ""),
            powerBIUrl: String(dto?.powerBIUrl ?? ""),
            attachmentName: String(dto?.attachments ?? ""),
            primarySME: String(dto?.primarySME ?? ""),
            alternateSME: String(dto?.alternateSME ?? ""),
            roleAccess,
            requestor: String(dto?.requester ?? dto?.createdBy ?? ""),
            createdDate: String(dto?.created ?? ""),
            launchDate: dto?.launchDate ? String(dto.launchDate) : undefined,
            reportId: String(dto?.reportId ?? ""),
            datasetId: String(dto?.dataSetId ?? dto?.dataSetID ?? dto?.datasetId ?? ""),
            navigationName: String(dto?.navigationName ?? "") || undefined,
          };
        };

        const mapped: CustomPowerBISetupRecord[] = list.map(mapDtoToRecord);

        if (!cancelled) setRecords(mapped);
      } catch (error: any) {
        if (!cancelled) {
          showSnackbarRef.current({
            message: "Failed to load custom Power BI reports.",
            severity: "error",
          });
        }
        console.error("Failed to load custom Power BI reports:", error);
      } finally {
        if (!cancelled) setRecordsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userGroups, userAssignments, userRoleTokens, isSystemAdmin]);

  const emptyDraft: RecordDraft = {
    powerBIName: "",
    powerBIUrl: "",
    attachmentFile: null,
    attachmentName: "",
    primarySME: "",
    alternateSME: "",
    roleAccess: [],
    reportId: "",
    datasetId: "",
    navigationName: "",
  };

  const [draft, setDraft] = useState<RecordDraft>(emptyDraft);

  const activeRecord = useMemo(() => {
    if (!activeRecordId) return null;
    return records.find((r) => r.id === activeRecordId) ?? null;
  }, [activeRecordId, records]);

  const filteredRecords = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return records;

    return records.filter((r) => {
      return (
        r.powerBIName.toLowerCase().includes(q) ||
        r.powerBIUrl.toLowerCase().includes(q) ||
        (r.attachmentName ?? "").toLowerCase().includes(q) ||
        r.primarySME.toLowerCase().includes(q) ||
        r.alternateSME.toLowerCase().includes(q) ||
        (r.roleAccess ?? []).join(", ").toLowerCase().includes(q) ||
        r.requestor.toLowerCase().includes(q) ||
        r.reportId.toLowerCase().includes(q) ||
        r.datasetId.toLowerCase().includes(q)
      );
    });
  }, [records, searchText]);

  const openAddDialog = () => {
    setDialogMode("add");
    setActiveRecordId(null);
    setDraft(emptyDraft);
    setAttachmentError("");
    setIsDialogOpen(true);
  };

  const openEditDialog = (record: CustomPowerBISetupRecord) => {
    setDialogMode("edit");
    setActiveRecordId(record.id);
    setDraft({
      powerBIName: record.powerBIName,
      powerBIUrl: record.powerBIUrl,
      attachmentFile: null,
      attachmentName: record.attachmentName ?? "",
      primarySME: record.primarySME,
      alternateSME: record.alternateSME,
      roleAccess: record.roleAccess ?? [],
      reportId: record.reportId,
      datasetId: record.datasetId,
      navigationName: record.navigationName ?? "",
    });
    setAttachmentError("");
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setActiveRecordId(null);
    setDialogMode("add");
    setDraft(emptyDraft);
    setAttachmentError("");
  };

  const isExcelFileName = (name?: string) => {
    const fileName = (name ?? "").trim().toLowerCase();
    return fileName.endsWith(".xlsx") || fileName.endsWith(".xls");
  };

  const applyAttachmentFile = (file: File | null) => {
    if (!file) {
      setDraft((p) => ({ ...p, attachmentFile: null, attachmentName: "" }));
      setAttachmentError("");
      return;
    }

    if (!isExcelFileName(file.name)) {
      const msg = "Attachment must be an Excel file (.xls or .xlsx).";
      setDraft((p) => ({ ...p, attachmentFile: null, attachmentName: "" }));
      setAttachmentError(msg);
      showSnackbar({ message: msg, severity: "error" });
      return;
    }

    setDraft((p) => ({ ...p, attachmentFile: file, attachmentName: file.name }));
    setAttachmentError("");
  };

  const validateDraft = (): string | null => {
    const sessionSiteId = getSiteIdFromSessionStorage();
    if (!sessionSiteId) return "Site ID is missing from session storage.";
    if (!draft.powerBIName.trim()) return "Power BI Name is required.";
    if (!draft.powerBIUrl.trim()) return "Power BI URL is required.";
    if (!draft.attachmentName?.trim() && !draft.attachmentFile)
      return "Attachment is required.";
    if (draft.attachmentName && !isExcelFileName(draft.attachmentName)) {
      return "Attachment must be an Excel file (.xls or .xlsx).";
    }
    if (!draft.primarySME.trim()) return "Primary SME is required.";
    if (!draft.alternateSME.trim()) return "Alternate SME is required.";
    if (!(draft.roleAccess ?? []).length) return "Role Access is required.";
    if (!draft.reportId.trim()) return "Report ID is required.";
    if (!draft.datasetId.trim()) return "Dataset ID is required.";
    return null;
  };

  const upsertRecord = (patch?: Partial<CustomPowerBISetupRecord>) => {
    const validationError = validateDraft();
    if (validationError) {
      showSnackbar({ message: validationError, severity: "error" });
      return false;
    }

    if (dialogMode === "add") {
      const sessionSiteId = getSiteIdFromSessionStorage();
      if (!sessionSiteId) {
        showSnackbar({
          message: "Site ID is missing from session storage.",
          severity: "error",
        });
        return false;
      }

      const now = new Date().toISOString();
      const newRecord: CustomPowerBISetupRecord = {
        id: uuidv4(),
        siteId: sessionSiteId,
        powerBIName: draft.powerBIName.trim(),
        powerBIUrl: draft.powerBIUrl.trim(),
        attachmentName: (
          draft.attachmentFile?.name ??
          draft.attachmentName ??
          ""
        ).trim(),
        primarySME: draft.primarySME.trim(),
        alternateSME: draft.alternateSME.trim(),
        roleAccess: draft.roleAccess ?? [],
        requestor: userEmail,
        createdDate: now,
        reportId: draft.reportId.trim(),
        datasetId: draft.datasetId.trim(),
        navigationName: draft.navigationName.trim() || undefined,
        ...patch,
      };

      setRecords((prev) => [newRecord, ...prev]);
      closeDialog();
      return true;
    }

    if (dialogMode === "edit" && activeRecord) {
      setRecords((prev) =>
        prev.map((r) => {
          if (r.id !== activeRecord.id) return r;

          const nextAttachmentName = (
            draft.attachmentFile?.name ??
            draft.attachmentName ??
            ""
          ).trim();

          return {
            ...r,
            powerBIName: isPowerUser ? r.powerBIName : draft.powerBIName.trim(),
            powerBIUrl: isPowerUser ? r.powerBIUrl : draft.powerBIUrl.trim(),
            attachmentName: nextAttachmentName,
            primarySME: isPowerUser ? r.primarySME : draft.primarySME.trim(),
            alternateSME: isPowerUser
              ? r.alternateSME
              : draft.alternateSME.trim(),
            roleAccess: isAttachmentOnlyEdit ? r.roleAccess : (draft.roleAccess ?? []),
            reportId: isPowerUser ? r.reportId : draft.reportId.trim(),
            datasetId: isPowerUser ? r.datasetId : draft.datasetId.trim(),
            navigationName: isAttachmentOnlyEdit ? r.navigationName : (draft.navigationName.trim() || undefined),
            ...patch,
          };
        }),
      );

      closeDialog();
      return true;
    }

    return false;
  };

  const handleSaveDraft = () => {
    if (saveLoading) return;

    (async () => {
      setSaveLoading(true);
      try {
        if (dialogMode === "edit" && activeRecord) {
          if (!isL2SupportUser && !(isPowerUser && dialogMode === "edit")) return;

          const validationError = validateDraft();
          if (validationError) {
            showSnackbar({ message: validationError, severity: "error" });
            return;
          }

          const idNumber = parseInt(activeRecord.id, 10);
          if (!idNumber || Number.isNaN(idNumber)) {
            showSnackbar({ message: "Invalid report id.", severity: "error" });
            return;
          }

          const siteId = parseInt(activeRecord.siteId, 10) || parseInt(getSiteIdFromSessionStorage() ?? "0", 10) || 0;

          const requestDto: CustomPowerBiRequestDTO = {
            hqSiteId: siteId,
            mdmClientId: activeRecord.mdmClientId ?? 0,
            powerBIName: draft.powerBIName.trim(),
            powerBIUrl: draft.powerBIUrl.trim(),
            attachments: (draft.attachmentFile?.name ?? draft.attachmentName ?? "").trim(),
            primarySME: draft.primarySME.trim(),
            alternateSME: draft.alternateSME.trim(),
            roleAccess: (isAttachmentOnlyEdit ? (activeRecord.roleAccess ?? []) : (draft.roleAccess ?? [])).join(","),
            requester: userEmail,
            reportId: draft.reportId.trim(),
            dataSetId: draft.datasetId.trim(),
            navigationName: draft.navigationName.trim(),
          };

          try {
            const updated = await taxPortalClientApi.reports_UpdateCustomPowerBiReportForSite(siteId, idNumber, requestDto);
            const dto: any = (updated as any)?.data?.data ?? (updated as any)?.data ?? updated;

            setRecords((prev) =>
              prev.map((r) => {
                if (r.id !== activeRecord.id) return r;
                return {
                  ...r,
                  mdmClientId:
                    typeof dto?.mdmClientId === "number"
                      ? dto.mdmClientId
                      : dto?.mdmClientId
                        ? parseInt(String(dto.mdmClientId), 10)
                        : r.mdmClientId,
                  powerBIName: String(dto?.powerBIName ?? r.powerBIName),
                  powerBIUrl: String(dto?.powerBIUrl ?? r.powerBIUrl),
                  attachmentName: String(dto?.attachments ?? r.attachmentName ?? ""),
                  primarySME: String(dto?.primarySME ?? r.primarySME),
                  alternateSME: String(dto?.alternateSME ?? r.alternateSME),
                  roleAccess:
                    isAttachmentOnlyEdit
                      ? r.roleAccess
                      : (parseRoleAccessFromDto(dto).length ? parseRoleAccessFromDto(dto) : (draft.roleAccess ?? [])),
                  requestor: String(dto?.requester ?? dto?.createdBy ?? r.requestor),
                  createdDate: String(dto?.created ?? r.createdDate),
                  launchDate: dto?.launchDate ? String(dto.launchDate) : r.launchDate,
                  reportId: String(dto?.reportId ?? r.reportId),
                  datasetId: String(dto?.dataSetId ?? dto?.dataSetID ?? r.datasetId),
                  navigationName: String(dto?.navigationName ?? requestDto.navigationName ?? "") || undefined,
                };
              })
            );

            closeDialog();
            showSuccessSnackbar({ message: "Saved." });
          } catch (error: any) {
            console.error("Failed to update custom Power BI report:", error);
            showSnackbar({ message: "Failed to save changes.", severity: "error" });
          }

          return;
        }

        // Add mode: create via API
        if (dialogMode === "add") {
          if (!isL2SupportUser) return;

          const validationError = validateDraft();
          if (validationError) {
            showSnackbar({ message: validationError, severity: "error" });
            return;
          }

          const sessionSiteId = getSiteIdFromSessionStorage();
          if (!sessionSiteId) {
            showSnackbar({
              message: "Site ID is missing from session storage.",
              severity: "error",
            });
            return;
          }

          const siteIdNumber = parseInt(sessionSiteId, 10);
          if (!siteIdNumber || Number.isNaN(siteIdNumber)) {
            showSnackbar({ message: "Invalid site id.", severity: "error" });
            return;
          }

          const requestDto: CustomPowerBiRequestDTO = {
            hqSiteId: siteHqSiteId ?? siteIdNumber,
            mdmClientId: siteMdmClientId ?? records.find((r) => r.mdmClientId != null)?.mdmClientId ?? 0,
            powerBIName: draft.powerBIName.trim(),
            powerBIUrl: draft.powerBIUrl.trim(),
            attachments: (draft.attachmentFile?.name ?? draft.attachmentName ?? "").trim(),
            primarySME: draft.primarySME.trim(),
            alternateSME: draft.alternateSME.trim(),
            roleAccess: (draft.roleAccess ?? []).join(","),
            requester: userEmail,
            reportId: draft.reportId.trim(),
            dataSetId: draft.datasetId.trim(),
            navigationName: draft.navigationName.trim(),
          };

          try {
            const created = await taxPortalClientApi.reports_AddCustomPowerBIReports(siteIdNumber, requestDto);
            const dto: any = (created as any)?.data?.data ?? (created as any)?.data ?? created;

            const next: CustomPowerBISetupRecord = {
              id: String(dto?.customPowerBiID ?? dto?.customPowerBiId ?? dto?.id ?? uuidv4()),
              siteId: String(dto?.hqSiteId ?? dto?.hqSiteID ?? siteIdNumber),
              mdmClientId:
                typeof dto?.mdmClientId === "number"
                  ? dto.mdmClientId
                  : dto?.mdmClientId
                    ? parseInt(String(dto.mdmClientId), 10)
                    : undefined,
              powerBIName: String(dto?.powerBIName ?? requestDto.powerBIName),
              powerBIUrl: String(dto?.powerBIUrl ?? requestDto.powerBIUrl),
              attachmentName: String(dto?.attachments ?? requestDto.attachments),
              primarySME: String(dto?.primarySME ?? requestDto.primarySME),
              alternateSME: String(dto?.alternateSME ?? requestDto.alternateSME),
              roleAccess: parseRoleAccessFromDto(dto).length ? parseRoleAccessFromDto(dto) : (draft.roleAccess ?? []),
              requestor: String(dto?.requester ?? dto?.createdBy ?? requestDto.requester ?? ""),
              createdDate: String(dto?.created ?? new Date().toISOString()),
              launchDate: dto?.launchDate ? String(dto.launchDate) : undefined,
              reportId: String(dto?.reportId ?? requestDto.reportId),
              datasetId: String(dto?.dataSetId ?? dto?.dataSetID ?? requestDto.dataSetId),
              navigationName: String(dto?.navigationName ?? requestDto.navigationName ?? "") || undefined,
            };

            setRecords((prev) => [next, ...prev]);
            closeDialog();
            showSuccessSnackbar({ message: "Saved." });
          } catch (error: any) {
            console.error("Failed to create custom Power BI report:", error);
            showSnackbar({ message: "Failed to save record.", severity: "error" });
          }

          return;
        }

        // Fallback (should not happen)
        const success = upsertRecord();
        if (success) showSuccessSnackbar({ message: "Saved." });
      } finally {
        setSaveLoading(false);
      }
    })();
  };

  const handleGenerateScriptFromDialog = async () => {
    if (!isL2SupportUser) return;

    if (dialogMode === "add") {
      const msg = "Cannot generate script for unsaved records. Please save the record first.";
      showSnackbar({ message: msg, severity: "warning" });
      return;
    }

    if (!activeRecord) {
      const msg = "No record found.";
      showSnackbar({ message: msg, severity: "error" });
      return;
    }

    setIsGenerateScriptDialogOpen(true);
    setGenerateScriptLoading(true);
    setGeneratedScriptText("");
    setGenerateScriptError("");

    try {
      const script = buildCustomPowerBiIframeScript(activeRecord);
      const normalized = String(script ?? "").trim();
      setGeneratedScriptText(normalized);
    } catch (error: any) {
      setGenerateScriptError(
        error?.message ? String(error.message) : "Failed to generate script.",
      );
    } finally {
      setGenerateScriptLoading(false);
    }
  };

  const closeGenerateScriptDialog = () => {
    setIsGenerateScriptDialogOpen(false);
    setGenerateScriptLoading(false);
    setGeneratedScriptText("");
    setGenerateScriptError("");
  };

  const buildCustomPowerBiIframeScript = (record: CustomPowerBISetupRecord): string => {
    const siteId = parseInt(record.siteId, 10);
    const reportId = parseInt(record.id, 10);

    if (!siteId || Number.isNaN(siteId) || !reportId || Number.isNaN(reportId)) {
      throw new Error("Invalid site ID or report ID.");
    }

    const base = `${window.location.origin}${window.location.pathname}`;
    const embedUrl = `${base}?siteID=${siteId}&PowerBIReport=${reportId}`;

    return [
      "<p>",
      "  <iframe",
      "    align=\"left\"",
      "    allowfullscreen=\"true\"",
      "    files=\"\"",
      "    frameborder=\"0\"",
      "    imanage=\"\"",
      "    referrerpolicy=\"unsafe-url\"",
      "    sandbox=\"allow-downloads allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation allow-top-navigation-by-user-activation allow-top-navigation-to-custom-protocols\"",
      "    scrolling=\"no\"",
      `    src="${embedUrl}"`,
      "    style=\"height:118vh ;width:100%; position:relative;\"",
      "  ></iframe>",
      "</p>",
    ].join("\n");
  };

  const openGenerateScriptDialog = async (record: CustomPowerBISetupRecord) => {
    if (!isL2SupportUser) return;

    setIsGenerateScriptDialogOpen(true);
    setGenerateScriptLoading(true);
    setGeneratedScriptText("");
    setGenerateScriptError("");

    try {
      const script = buildCustomPowerBiIframeScript(record);
      const normalized = String(script ?? "").trim();
      setGeneratedScriptText(normalized);
    } catch (error: any) {
      setGenerateScriptError(
        error?.message ? String(error.message) : "Failed to generate script.",
      );
    } finally {
      setGenerateScriptLoading(false);
    }
  };

 const copyGeneratedScript = async () => {
    const text = (generatedScriptText || "").trim();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      showSuccessSnackbar({ message: "Copied." });
    } catch {
      showSnackbar({ message: "Failed to copy to clipboard.", severity: "error" });
    }
  };


  const handleRefresh = async (record: CustomPowerBISetupRecord) => {
    if (!canEditAndRefresh) return;
    
    const customPowerBIId = parseInt(record.id, 10);
    const siteId =
      parseInt(record.siteId, 10) ||
      parseInt(getSiteIdFromSessionStorage() ?? "0", 10);

    if (!siteId || Number.isNaN(siteId)) {
      showSnackbar({ message: "Invalid site id.", severity: "error" });
      return;
    }

    if (!customPowerBIId || Number.isNaN(customPowerBIId)) {
      showSnackbar({ message: "Invalid report id.", severity: "error" });
      return;
    }

    try {
      const refreshed = await taxPortalClientApi.reports_RefreshCustomPowerBiDataSet(siteId, customPowerBIId);

      if (refreshed !== true) {
        showSnackbar({ message: "Dataset refresh did not complete.", severity: "error" });
        return;
      }

      const now = new Date().toISOString();
      setRecords((prev) =>
        prev.map((r) => {
          if (r.id !== record.id) return r;
          return { ...r, lastRefreshedDate: now };
        }),
      );

      showSuccessSnackbar({ message: "Refresh completed successfully." });
    } catch (error: any) {
      showSnackbar({
        message: error?.message ?? "Failed to refresh dataset.",
        severity: "error",
      });
    }
  };

  const handleInlineSave = (recordId: string) => {
    if (!isL2SupportUser) return;
    showSuccessSnackbar({ message: "Saved." });
  };

  const openDeleteConfirm = (record: CustomPowerBISetupRecord) => {
    if (!isL2SupportUser) return;
    setDeleteTarget(record);
    setIsDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    if (deleteLoading) return;

    setIsDeleteConfirmOpen(false);
    setDeleteTarget(null);
  };

  const forceCloseDeleteConfirm = () => {

    setIsDeleteConfirmOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);

    try {
      const id = parseInt(deleteTarget.id, 10);
      if (!id || Number.isNaN(id)) {
        throw new Error("Invalid report id.");
      }

      const result: CustomPowerBiDeleteResponse = await taxPortalClientApi.reports_DeleteCustomPowerBiReport(id);
      const resultAny = result as any;
      const reportFound =
        result?.reportFound ??
        (typeof resultAny?.ReportFound === "boolean" ? resultAny.ReportFound : undefined);
      const noReportFound =
        result?.noReportFound ??
        (typeof resultAny?.NoReportFound === "boolean" ? resultAny.NoReportFound : undefined);
      const responseMessage =
        result?.message ??
        (typeof resultAny?.Message === "string" ? resultAny.Message : undefined);

      if (reportFound === false || noReportFound === true) {
        // Keep UI in sync when backend says the row no longer exists.
        setRecords((prev) => prev.filter((x) => x.id !== deleteTarget.id));
        showSnackbar({
          message: responseMessage ?? "Custom Power BI report not found.",
          severity: "error",
        });
        forceCloseDeleteConfirm();
        return;
      }

      setRecords((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      forceCloseDeleteConfirm();

      showSuccessSnackbar({
        message: responseMessage ?? "Custom Power BI report deleted successfully.",
        severity: "success",
      });
    } catch (error: any) {
      showSnackbar({
        message:
          error?.message ??
          "Navigation delete API failed. Please try again.",
        severity: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const openActionsMenu = (evt: React.MouseEvent<HTMLElement>, record: CustomPowerBISetupRecord) => {
    const rect = (evt.currentTarget as HTMLElement).getBoundingClientRect();
    setActionsAnchorPosition({ top: rect.bottom, left: rect.right });
    setActionsRecord(record);
  };

  const closeActionsMenu = () => {
    setActionsAnchorPosition(null);
    setActionsRecord(null);
  };

  const renderActionsCell = (cell: any) => {
    const record: CustomPowerBISetupRecord = cell.data;

    return (
      <div className="custom-pbi-setup__actionCell">
        <IconButton
          size="small"
          className="custom-pbi-setup__actionMenuButton"
          onClick={(e) => openActionsMenu(e, record)}
          aria-label="Actions"
          aria-controls={isActionsMenuOpen ? "custom-pbi-setup-actions-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={isActionsMenuOpen ? "true" : undefined}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </div>
    );
  };

  const openCustomReportEmbedUrl = (record: CustomPowerBISetupRecord) => {
    const siteId = parseInt(record.siteId, 10);
    const customReportId = parseInt(record.id, 10);

    if (!siteId || Number.isNaN(siteId) || !customReportId || Number.isNaN(customReportId)) {
      const fallbackUrl = String(record.powerBIUrl ?? "").trim();
      if (!fallbackUrl) {
        showSnackbar({ message: "No report URL available.", severity: "error" });
        return;
      }

      window.open(fallbackUrl, "_blank", "noopener,noreferrer");
      return;
    }

    const deepLinkUrl = `${window.location.origin}${window.location.pathname}?siteID=${siteId}&PowerBIReport=${customReportId}`;
    window.open(deepLinkUrl, "_blank", "noopener,noreferrer");
  };

  const isEditMode = dialogMode === "edit";
  const reportsLimitExceeded = records.length >= 4;
  const canGenerateScriptInDialog = isL2SupportUser;
  const isAttachmentOnlyEdit = isPowerUser && isEditMode;

  const dialogTitle =
    dialogMode === "add"
      ? "Add record - Custom Power BI Setup"
      : "Edit record - Custom Power BI Setup";

  return (
    <div className="custom-pbi-setup">
      {isEmbeddedReportView ? (
        <div className="custom-pbi-setup__embeddedReportView">
          <div className="custom-pbi-setup__embeddedReportContainer">
            <PowerBIEmbed
              embedConfig={embeddedReportConfigDTO}
              cssClassName="report-style-class"
              getEmbeddedComponent={() => {
                // Report component is loaded
              }}
            />
          </div>
        </div>
      ) : isDeepLinkLoading ? (
        <div className="custom-pbi-setup__loadingState">Loading PowerBI report...</div>
      ) : isDeepLinkAccessDenied ? (
        <AccessDenied />
      ) : (
        <>
          <div className="custom-pbi-setup__titleRow">
            <h2>Custom Power BI Set Up</h2>
          </div>
        <div>
          <p> Create and manage up to 4 custom Power BI reports with their own navigation items.</p>
          <p> L2 Support team creates and manages the navigation items.</p>
        </div>
          <DataGrid
        {...dataGridConfig}
        dataSource={filteredRecords}
        ref={dataGridRef}
        showBorders={true}
        height={calculateGridHeight(260)}
        noDataText={recordsLoading ? "Loading..." : "No Data"}
        remoteOperations={true}
        columnResizingMode="widget"
      >
        <Toolbar>
          <Item key="search" location="after">
            <TextField
              size="small"
              variant="standard"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                minWidth: 220,
                "& .MuiInputBase-root": { height: 36, fontSize: "14px" },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "rgba(var(--cui-color-base-rgb), 0.65)" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Item>

          {isL2SupportUser && (
            <Item key="add" location="after">
              <Button
                variant="outlined"
                size="small"
                onClick={openAddDialog}
                sx={{ height: 36, fontSize: "14px" }}
                disabled={reportsLimitExceeded}
              >
                ADD
              </Button>
            </Item>
          )}
        </Toolbar>

        <LoadPanel enabled={false} />
        <Scrolling columnRenderingMode="virtual" />
        <FilterRow visible={true} />
        <HeaderFilter visible={true} />

        <Column minWidth={180} dataField="powerBIName" caption="Power BI Name" alignment="left" />
        <Column
          minWidth={250}
          dataField="powerBIUrl"
          caption="Power BI URL"
          alignment="left"
          cellRender={(e: any) => (
            <a
              className="hyper-link-blue"
              href={e.data.powerBIUrl}
              onClick={(evt) => {
                evt.preventDefault();
                openCustomReportEmbedUrl(e.data as CustomPowerBISetupRecord);
              }}
              target="_blank"
              rel="noreferrer"
            >
              {e.data.powerBIUrl}
            </a>
          )}
        />
        <Column minWidth={160} dataField="attachmentName" caption="Attachments" alignment="left" />
        <Column minWidth={160} dataField="primarySME" caption="Primary SME" alignment="left" />
        <Column minWidth={160} dataField="alternateSME" caption="Alternate SME" alignment="left" />
        <Column
          minWidth={200}
          dataField="roleAccess"
          caption="Role Access"
          alignment="left"
          calculateCellValue={(rowData: CustomPowerBISetupRecord) =>
            (rowData.roleAccess ?? []).join(", ")
          }
        />
        <Column minWidth={180} dataField="requestor" caption="Requester" alignment="left" />
        <Column minWidth={180} dataField="navigationName" caption="Navigation Assignment" alignment="left"/>
        <Column caption="Actions" width={120} alignment="center" cellRender={renderActionsCell} />
      </DataGrid>

        <Menu
          id="custom-pbi-setup-actions-menu"
          anchorReference="anchorPosition"
          anchorPosition={actionsAnchorPosition ?? undefined}
          open={isActionsMenuOpen}
          onClose={closeActionsMenu}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          MenuListProps={{ dense: true }}
        >
          <MenuItem
            onClick={() => {
              if (actionsRecord) openEditDialog(actionsRecord);
              closeActionsMenu();
            }}
            disabled={!canEditAndRefresh}
          >
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Edit
          </MenuItem>

          <MenuItem
            onClick={() => {
              if (actionsRecord) void handleRefresh(actionsRecord);
              closeActionsMenu();
            }}
            disabled={!canEditAndRefresh}
          >
            <ListItemIcon>
              <RefreshOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Refresh
          </MenuItem>

          {isL2SupportUser && (
            <MenuItem
              onClick={() => {
                if (actionsRecord) handleInlineSave(actionsRecord.id);
                closeActionsMenu();
              }}
            >
              <ListItemIcon>
                <SaveOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Save
            </MenuItem>
          )}

          {isL2SupportUser && (
            <MenuItem
              onClick={() => {
                if (actionsRecord) openGenerateScriptDialog(actionsRecord);
                closeActionsMenu();
              }}
            >
              <ListItemIcon>
                <RocketLaunchOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Generate Script
            </MenuItem>
          )}

          {isL2SupportUser && (
            <MenuItem
              onClick={() => {
                const record = actionsRecord;
                closeActionsMenu();
                if (record) openDeleteConfirm(record);
              }}
            >
              <ListItemIcon>
                <DeleteOutlineIcon fontSize="small" />
              </ListItemIcon>
              Delete
            </MenuItem>
          )}
        </Menu>

        <Dialog
          open={isDeleteConfirmOpen}
          onClose={handleCloseDeleteConfirm}
          fullWidth
          maxWidth="sm"
          className="cui-c_modal"
        >
          <DialogTitle className="cui-c_modal-title">Confirm Delete</DialogTitle>
          <DialogContent className="custom-pbi-setup__dialogContent">
            Are you sure you would like to delete {deleteTarget?.powerBIName ? (
              <b>{deleteTarget.powerBIName}</b>
            ) : (
              <b>this Power BI record</b>
            )}?
          </DialogContent>
          <DialogActions className="cui-c_modal-footer">
            <Button variant="outlined" onClick={handleCloseDeleteConfirm} disabled={deleteLoading}>
              No
            </Button>
            <Button variant="outlined" onClick={handleDeleteConfirm} disabled={deleteLoading || !deleteTarget}>
              Yes
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          scroll="body"
          open={isGenerateScriptDialogOpen}
          onClose={closeGenerateScriptDialog}
          fullWidth
          maxWidth="md"
          className="cui-c_modal custom-pbi-setup__generateScriptDialog"
        >
          <DialogTitle className="cui-c_modal-title">Generate Script</DialogTitle>
          <DialogContent className="custom-pbi-setup__dialogContent custom-pbi-setup__generateScriptContent">
           <TextField
  className="custom-pbi-setup__scriptField"
  fullWidth
  multiline
  value={generateScriptLoading ? "Generating…" : generatedScriptText}
  placeholder="Generated script will appear here."
  InputProps={{
    readOnly: true,
  }}
  sx={{
    "& .MuiInputBase-root": {
      alignItems: "flex-start",
    },
    "& textarea": {
      maxHeight: "400px",
      overflowY: "auto !important",
    },
  }}
  variant="outlined"
/>
          </DialogContent>
          <DialogActions className="cui-c_modal-footer">
            <Button variant="outlined" onClick={closeGenerateScriptDialog}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={copyGeneratedScript}
              disabled={generateScriptLoading || !(generatedScriptText || "").trim()}
            >
              Copy
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isDialogOpen}
          onClose={closeDialog}
          fullWidth
          maxWidth="md"
          className="cui-c_modal"
        >
          <DialogTitle className="cui-c_modal-title">{dialogTitle}</DialogTitle>
          <DialogContent className="custom-pbi-setup__dialogContent">
            <div className="custom-pbi-setup__field">
              <label className="custom-pbi-setup__fieldLabel" htmlFor="custom-pbi-setup-powerbi-name">
                Power BI Name
              </label>
              <TextField
                id="custom-pbi-setup-powerbi-name"
                value={draft.powerBIName}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, powerBIName: e.target.value }))
                }
                disabled={isAttachmentOnlyEdit}
                fullWidth
                size="small"
                margin="none"
              />
            </div>

            <div className="custom-pbi-setup__field">
              <label className="custom-pbi-setup__fieldLabel" htmlFor="custom-pbi-setup-powerbi-url">
                Power BI URL
              </label>
              <TextField
                id="custom-pbi-setup-powerbi-url"
                value={draft.powerBIUrl}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, powerBIUrl: e.target.value }))
                }
                disabled={isAttachmentOnlyEdit}
                fullWidth
                size="small"
                margin="none"
              />
            </div>

            <div className="custom-pbi-setup__attachmentSection">
              <div className="custom-pbi-setup__attachmentLabel">
                <span>Attachment</span>
                <InfoOutlinedIcon
                  fontSize="small"
                  className="custom-pbi-setup__infoIcon"
                />
              </div>

              <div
                className="custom-pbi-setup__dropzone"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  const file = e.dataTransfer.files?.[0] ?? null;
                  applyAttachmentFile(file);
                }}
              >
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  component="label"
                >
                  Browse
                  <input
                    type="file"
                    hidden
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null;
                      applyAttachmentFile(file);
                      e.currentTarget.value = "";
                    }}
                  />
                </Button>

                <span className="custom-pbi-setup__dropzoneText">
                  or drag files to upload
                </span>
              </div>

              <div className="custom-pbi-setup__attachmentName">
                {draft.attachmentName || ""}
              </div>

              {attachmentError && (
                <div className="custom-pbi-setup__attachmentError">
                  {attachmentError}
                </div>
              )}
            </div>

            <div className="custom-pbi-setup__field">
              <label className="custom-pbi-setup__fieldLabel" htmlFor="custom-pbi-setup-primary-sme">
                Primary SME
              </label>
              <TextField
                id="custom-pbi-setup-primary-sme"
                value={draft.primarySME}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, primarySME: e.target.value }))
                }
                disabled={isAttachmentOnlyEdit}
                fullWidth
                size="small"
                margin="none"
              />
            </div>

            <div className="custom-pbi-setup__field">
              <label className="custom-pbi-setup__fieldLabel" htmlFor="custom-pbi-setup-alternate-sme">
                Alternate SME
              </label>
              <TextField
                id="custom-pbi-setup-alternate-sme"
                value={draft.alternateSME}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, alternateSME: e.target.value }))
                }
                disabled={isAttachmentOnlyEdit}
                fullWidth
                size="small"
                margin="none"
              />
            </div>

            <div className="custom-pbi-setup__field">
              <label className="custom-pbi-setup__fieldLabel" htmlFor="custom-pbi-setup-role-access">
                Role Access
              </label>
              <TextField
                id="custom-pbi-setup-role-access"
                select
                value={draft.roleAccess}
                onChange={(e) => {
                  const value = e.target.value;
                  const next = Array.isArray(value)
                    ? (value as string[])
                    : String(value ?? "")
                        .split(",")
                        .map((v) => v.trim())
                        .filter(Boolean);
                  setDraft((p) => ({ ...p, roleAccess: next }));
                }}
                disabled={isAttachmentOnlyEdit || isRoleAccessLoading}
                fullWidth
                size="small"
                margin="none"
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => (selected as string[]).join(", "),
                  MenuProps: {
                    disablePortal: true,
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    PaperProps: {
                      style: {
                        maxHeight: 230,
                        overflowY: "auto",
                      },
                    },
                  },
                }}
              >
                {roleAccessOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </TextField>
            </div>

            <div className="custom-pbi-setup__field">
              <label className="custom-pbi-setup__fieldLabel" htmlFor="custom-pbi-setup-report-id">
                Report ID
              </label>
              <TextField
                id="custom-pbi-setup-report-id"
                value={draft.reportId}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, reportId: e.target.value }))
                }
                disabled={isAttachmentOnlyEdit}
                fullWidth
                size="small"
                margin="none"
              />
            </div>

            <div className="custom-pbi-setup__field">
              <label className="custom-pbi-setup__fieldLabel" htmlFor="custom-pbi-setup-dataset-id">
                Dataset ID
              </label>
              <TextField
                id="custom-pbi-setup-dataset-id"
                value={draft.datasetId}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, datasetId: e.target.value }))
                }
                disabled={isAttachmentOnlyEdit}
                fullWidth
                size="small"
                margin="none"
              />
            </div>

            <div className="custom-pbi-setup__field">
              <label className="custom-pbi-setup__fieldLabel" htmlFor="custom-pbi-setup-navigation-name">
                Navigation Assignment
              </label>
              <TextField
                id="custom-pbi-setup-navigation-name"
                value={draft.navigationName}
                onChange={(e) =>
                  setDraft((p) => ({ ...p, navigationName: e.target.value }))
                }
                disabled={isAttachmentOnlyEdit}
                fullWidth
                size="small"
                margin="none"
              />
            </div>

          </DialogContent>

          <DialogActions className="cui-c_modal-footer">
            <Button variant="outlined" onClick={closeDialog}>
              Cancel
            </Button>

            <Button
              variant="outlined"
              onClick={handleSaveDraft}
              disabled={saveLoading || (!isL2SupportUser && !(isPowerUser && isEditMode))}
            >
              Save
            </Button>

            {canGenerateScriptInDialog && (
              <Button
                variant="outlined"
                onClick={handleGenerateScriptFromDialog}
                disabled={saveLoading || !isL2SupportUser}
              >
                Generate Script
              </Button>
            )}
          </DialogActions>
        </Dialog>
        </>
      )}
    </div>
  );
};

export default CustomPowerBISetup;
